import React, { useState } from 'react';
import { Server, Plus, Edit2, Save, X, Copy, Check, ExternalLink, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Loading } from '../../components/ui/Loading';
import { EmptyState } from '../../components/ui/EmptyState';
import { useProviderConfigs, useUpdateProviderConfig, useIntegrationGuide } from '../../hooks/useSettings';
import { UpdateProviderConfigRequest } from '../../types';
import toast from 'react-hot-toast';

// Provider-specific settings configuration
const PROVIDER_SETTINGS_FIELDS: Record<string, Array<{ key: string; label: string; type: string; placeholder?: string; help?: string }>> = {
    stripe: [
        { key: 'api_version', label: 'API Version', type: 'text', placeholder: '2023-10-16', help: 'Stripe API version (optional)' },
    ],
    iyzico: [
        { key: 'merchant_id', label: 'Merchant ID', type: 'text', placeholder: '123456' },
        { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'sandbox-xxx' },
    ],
    paytr: [
        { key: 'merchant_id', label: 'Merchant ID', type: 'text', placeholder: '123456' },
        { key: 'merchant_key', label: 'Merchant Key', type: 'password', placeholder: 'xxx' },
    ],
    paypal: [
        { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'xxx' },
        { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'xxx' },
    ],
    adyen: [
        { key: 'merchant_account', label: 'Merchant Account', type: 'text', placeholder: 'YourMerchantAccount' },
        { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'xxx' },
    ],
    paddle: [
        { key: 'vendor_id', label: 'Vendor ID', type: 'text', placeholder: '12345' },
    ],
};

// Critical integration information for each provider
const PROVIDER_INTEGRATION_INFO: Record<string, { title: string; field: string; code: string; description: string }> = {
    stripe: {
        title: 'Payment Intent Metadata',
        field: 'metadata.order_id',
        code: `$intent = \\Stripe\\PaymentIntent::create([
  'amount' => 10000,
  'currency' => 'usd',
  'metadata' => [
    'order_id' => 'order_12345'  // ← CRITICAL for matching
  ]
]);`,
        description: 'Use metadata.order_id field to match payments with your orders',
    },
    iyzico: {
        title: 'Conversation ID',
        field: 'conversationId',
        code: `$payment = \\Iyzipay\\Model\\Payment::create([
  'price' => '100.00',
  'paidPrice' => '100.00',
  'conversationId' => 'order_12345',  // ← CRITICAL for matching
  // ...
]);`,
        description: 'Use conversationId to match Iyzico payments with your orders',
    },
    paytr: {
        title: 'Merchant Order ID',
        field: 'merchant_oid',
        code: `$params = [
  'merchant_id' => '123456',
  'merchant_oid' => 'order_12345',  // ← CRITICAL: Your order ID
  // ...
];`,
        description: 'Use merchant_oid to uniquely identify your orders in PayTR',
    },
    paypal: {
        title: 'Custom ID',
        field: 'custom_id',
        code: `const order = await paypal.orders.create({
  purchase_units: [{
    amount: { value: '100.00', currency_code: 'USD' },
    custom_id: 'order_12345'  // ← CRITICAL for matching
  }]
});`,
        description: 'Use custom_id in purchase_units to match PayPal payments',
    },
    adyen: {
        title: 'Merchant Reference',
        field: 'merchantReference',
        code: `const payment = await adyen.checkout.payments({
  amount: { value: 10000, currency: 'EUR' },
  merchantReference: 'order_12345',  // ← CRITICAL for matching
  // ...
});`,
        description: 'Use merchantReference to uniquely identify transactions',
    },
    paddle: {
        title: 'Passthrough Data',
        field: 'passthrough.order_id',
        code: `Paddle.Checkout.open({
  product: 12345,
  passthrough: JSON.stringify({
    order_id: 'order_12345'  // ← CRITICAL for matching
  })
});`,
        description: 'Use passthrough.order_id to match Paddle payments with orders',
    },
    braintree: {
        title: 'SDK Integration Only',
        field: 'N/A',
        code: `// Braintree doesn't support webhook signatures
// Use SDK integration for payment tracking:
const result = await gateway.transaction.sale({
  amount: '100.00',
  orderId: 'order_12345',  // ← Use this for tracking
  // ...
});`,
        description: '⚠️ Braintree webhooks do not support signature verification. Use SDK for secure integration.',
    },
    custom: {
        title: 'Custom Provider Integration',
        field: 'transaction_id',
        code: `// Custom provider - implement your own logic
// Make sure to send a unique transaction ID:
POST /api/v1/sdk/events
{
  "transaction_id": "order_12345",
  "event_type": "payment_success",
  // ...
}`,
        description: '⚠️ Custom providers require SDK integration. No webhook signature support available.',
    },
};

const Providers: React.FC = () => {
    const { data: providerData, isLoading } = useProviderConfigs();
    const { data: integrationGuide, isLoading: guideLoading } = useIntegrationGuide();
    const updateMutation = useUpdateProviderConfig();

    const [showModal, setShowModal] = useState(false);
    const [editingConfig, setEditingConfig] = useState<any>(null);
    const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
    const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
    const [formData, setFormData] = useState<UpdateProviderConfigRequest & { id?: number }>({
        provider: 'stripe',
        environment: 'test',
        webhook_secret: '',
        is_active: true,
        settings: {},
    });

    const handleCopyUrl = (url: string, provider: string) => {
        navigator.clipboard.writeText(url);
        setCopiedUrl(provider);
        toast.success('Webhook URL copied!');
        setTimeout(() => setCopiedUrl(null), 2000);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Check provider limits before submitting
        if (!editingConfig && providerData?.limits && !providerData.limits.can_add_more) {
            toast.error('Provider limit reached. Please upgrade your plan to add more providers.');
            return;
        }
        
        try {
            await updateMutation.mutateAsync(formData);
            setShowModal(false);
            setEditingConfig(null);
            setFormData({
                provider: 'stripe',
                environment: 'test',
                webhook_secret: '',
                is_active: true,
                settings: {},
            });
        } catch (error) {
            // Error is already handled by mutation onError
            // Just keep the modal open so user can see the error toast
        }
    };

    const handleEdit = (config: any) => {
        setEditingConfig(config);
        setFormData({
            id: config.id,
            provider: config.provider,
            environment: config.environment,
            webhook_secret: config.webhook_secret || '',
            is_active: config.is_active,
            settings: config.settings || {},
        });
        setShowModal(true);
    };

    const handleAddNew = () => {
        setEditingConfig(null);
        const firstProvider = providerData?.available[0]?.value || 'stripe';
        setFormData({
            provider: firstProvider,
            environment: 'test',
            webhook_secret: '',
            is_active: true,
            settings: {},
        });
        setShowModal(true);
    };

    const handleSettingChange = (key: string, value: string) => {
        setFormData({
            ...formData,
            settings: {
                ...formData.settings,
                [key]: value,
            },
        });
    };

    if (isLoading) {
        return <Loading fullScreen message="Loading provider configurations..." />;
    }

    const selectedProviderInfo = providerData?.available.find(p => p.value === formData.provider);
    const settingsFields = PROVIDER_SETTINGS_FIELDS[formData.provider] || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Provider Settings</h1>
                    <p className="text-gray-600 mt-1">Configure payment provider webhooks and integrations</p>
                    {providerData?.limits && (
                        <p className="text-sm text-gray-500 mt-1">
                            {providerData.limits.used} / {providerData.limits.max} providers configured
                        </p>
                    )}
                </div>
                <button 
                    onClick={handleAddNew} 
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!providerData?.limits?.can_add_more}
                    title={!providerData?.limits?.can_add_more ? 'Provider limit reached' : 'Add new provider'}
                >
                    <Plus className="w-5 h-5" />
                    Add Provider
                </button>
            </div>

            {/* Provider Limit Warning */}
            {providerData?.limits && !providerData.limits.can_add_more && providerData.limits.upgrade_for_more && (
                <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <Info className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-orange-900 mb-1">
                                Provider Limit Reached
                            </h3>
                            <p className="text-sm text-orange-800 mb-2">
                                Your <strong>{providerData.limits.max === 2 ? 'Free' : 
                                providerData.limits.upgrade_for_more.plan.charAt(0).toUpperCase() + 
                                providerData.limits.upgrade_for_more.plan.slice(1)}</strong> plan allows up to{' '}
                                <strong>{providerData.limits.max} providers</strong>. 
                                You've configured {providerData.limits.used} provider{providerData.limits.used !== 1 ? 's' : ''}.
                            </p>
                            <p className="text-sm text-orange-700 mb-3">
                                Upgrade to <strong>{providerData.limits.upgrade_for_more.plan_name}</strong> plan 
                                to add up to <strong>{providerData.limits.upgrade_for_more.max_providers} providers</strong>.
                            </p>
                            <div className="flex gap-2">
                                <a
                                    href="/pricing"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                                >
                                    View Pricing Plans
                                </a>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Provider Configs */}
            <div className="grid grid-cols-1 gap-6">
                {!providerData?.configured || providerData.configured.length === 0 ? (
                    <EmptyState
                        icon={Server}
                        title="No providers configured"
                        description="Add your first payment provider to start monitoring webhooks"
                    />
                ) : (
                    providerData.configured.map((config) => (
                        <Card key={config.id} className="hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                                        <Server className="w-6 h-6 text-primary-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{config.provider_label}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <Badge className={config.environment === 'production' ? 'text-green-700 bg-green-100' : 'text-yellow-700 bg-yellow-100'}>
                                                {config.environment}
                                            </Badge>
                                            <Badge className={config.is_active ? 'text-green-700 bg-green-100' : 'text-gray-700 bg-gray-100'}>
                                                {config.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                            {providerData.available.find(p => p.value === config.provider)?.supports_webhook_signature && (
                                                <Badge className="text-blue-700 bg-blue-100">✓ Signature</Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleEdit(config)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Edit configuration"
                                >
                                    <Edit2 className="w-4 h-4 text-gray-600" />
                                </button>
                            </div>

                            {/* Webhook URL */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-sm font-medium text-gray-700">Webhook URL</label>
                                    <button
                                        onClick={() => handleCopyUrl(config.webhook_url, config.provider)}
                                        className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
                                    >
                                        {copiedUrl === config.provider ? (
                                            <>
                                                <Check className="w-3 h-3" />
                                                Copied!
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-3 h-3" />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <code className="text-xs text-gray-600 break-all block">
                                    {config.webhook_url}
                                </code>
                            </div>

                            {/* Config Details */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-gray-600">Webhook Secret</span>
                                    <p className="font-mono text-gray-900 mt-1">
                                        {config.has_webhook_secret ? '••••••••' : 'Not configured'}
                                    </p>
                                </div>
                                {config.settings && Object.keys(config.settings).length > 0 && (
                                    <div>
                                        <span className="text-gray-600">Additional Settings</span>
                                        <p className="text-gray-900 mt-1">
                                            {Object.keys(config.settings).length} field(s) configured
                                        </p>
                                    </div>
                                )}
                                {config.updated_at && (
                                    <div>
                                        <span className="text-gray-600">Last Updated</span>
                                        <p className="text-gray-900 mt-1">
                                            {new Date(config.updated_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                                {config.created_at && (
                                    <div>
                                        <span className="text-gray-600">Created</span>
                                        <p className="text-gray-900 mt-1">
                                            {new Date(config.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </Card>
                    ))
                )}
            </div>

            {/* Available Providers Info */}
            {providerData?.available && providerData.available.length > 0 && (
                <Card>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Providers</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {providerData.available.map((provider) => (
                            <div key={provider.value} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                                <Server className="w-4 h-4 text-gray-600" />
                                <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-900">{provider.label}</span>
                                    {provider.supports_webhook_signature && (
                                        <span className="ml-1 text-xs text-green-600">✓</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* Integration Guide */}
            {guideLoading ? (
                <Card>
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-sm text-gray-600">Loading integration guide...</span>
                    </div>
                </Card>
            ) : integrationGuide && integrationGuide.providers.length > 0 && (
                <Card>
                    <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Integration Guide</h2>
                        <p className="text-sm text-gray-600">
                            Click on a provider to see integration details and required fields for SDK implementation
                        </p>
                    </div>

                    <div className="space-y-3">
                        {integrationGuide.providers.filter(p => p.supported).map((provider) => (
                            <div key={provider.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* Provider Header */}
                                <button
                                    onClick={() => setExpandedProvider(expandedProvider === provider.id ? null : provider.id)}
                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <Server className="w-5 h-5 text-gray-600" />
                                        <div className="text-left">
                                            <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                                            <p className="text-xs text-gray-500">
                                                Matching field: <code className="text-blue-600">{provider.integration.matching_field.name}</code>
                                            </p>
                                        </div>
                                    </div>
                                    {expandedProvider === provider.id ? (
                                        <ChevronUp className="w-5 h-5 text-gray-400" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                    )}
                                </button>

                                {/* Expanded Content */}
                                {expandedProvider === provider.id && (
                                    <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-4">
                                        {/* Matching Field Info */}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">📍 Matching Field</h4>
                                            <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                                <div className="flex items-start justify-between mb-2">
                                                    <code className="text-sm font-mono text-blue-600">
                                                        {provider.integration.matching_field.name}
                                                    </code>
                                                    <Badge className="text-xs">Required</Badge>
                                                </div>
                                                <p className="text-xs text-gray-600">{provider.integration.matching_field.description}</p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Source: {provider.integration.matching_field.source}
                                                </p>
                                            </div>
                                        </div>

                                        {/* SDK Payload Example */}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">📤 SDK Payload Example</h4>
                                            <div className="p-3 bg-gray-900 rounded-lg overflow-x-auto">
                                                <pre className="text-xs text-green-400 font-mono">
                                                    {JSON.stringify(provider.integration.sdk_payload, null, 2)}
                                                </pre>
                                            </div>
                                        </div>

                                        {/* Provider Configuration */}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                                ⚙️ Provider Configuration - {provider.integration.provider_config.field}
                                            </h4>
                                            <p className="text-xs text-gray-600 mb-2">
                                                {provider.integration.provider_config.description}
                                            </p>
                                            <div className="p-3 bg-gray-900 rounded-lg overflow-x-auto">
                                                <pre className="text-xs text-green-400 font-mono">
                                                    {provider.integration.provider_config.example.code}
                                                </pre>
                                            </div>
                                        </div>

                                        {/* Webhook Fields */}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900 mb-2">🔔 Webhook Fields</h4>
                                            <div className="space-y-2">
                                                {Object.entries(provider.integration.webhook_fields).map(([key, value]) => (
                                                    <div key={key} className="p-2 bg-white border border-gray-200 rounded">
                                                        <div className="flex items-center justify-between">
                                                            <code className="text-xs font-mono text-blue-600">{key}</code>
                                                            <span className="text-xs text-gray-500">→</span>
                                                        </div>
                                                        <p className="text-xs text-gray-600 mt-1">{value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Documentation Link */}
                                        <a
                                            href={provider.documentation_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            View Official Documentation
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Common SDK Fields */}
                    {integrationGuide.common_sdk_fields && integrationGuide.common_sdk_fields.length > 0 && (
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">📋 Common SDK Fields (All Providers)</h4>
                            <div className="space-y-2">
                                {integrationGuide.common_sdk_fields.map((field) => (
                                    <div key={field.field} className="flex items-start gap-3">
                                        <code className="text-xs font-mono text-blue-600 bg-white px-2 py-1 rounded">
                                            {field.field}
                                        </code>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <Badge className="text-xs">{field.type}</Badge>
                                                {field.required && <Badge className="text-xs bg-red-100 text-red-700">Required</Badge>}
                                            </div>
                                            <p className="text-xs text-gray-600 mt-1">{field.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
                    <Card className="max-w-2xl w-full my-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingConfig ? 'Edit Provider' : 'Add Provider'}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Configure webhook and provider-specific settings
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setEditingConfig(null);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Basic Settings */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Provider *
                                    </label>
                                    <select
                                        value={formData.provider}
                                        onChange={(e) => setFormData({ ...formData, provider: e.target.value, settings: {} })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={!!editingConfig}
                                        required
                                    >
                                        {providerData?.available.map((provider) => (
                                            <option key={provider.value} value={provider.value}>
                                                {provider.label}
                                            </option>
                                        ))}
                                    </select>
                                    {editingConfig && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Provider cannot be changed
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Environment *
                                    </label>
                                    <select
                                        value={formData.environment}
                                        onChange={(e) => setFormData({ ...formData, environment: e.target.value as 'test' | 'production' })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        disabled={!!editingConfig}
                                        required
                                    >
                                        <option value="test">Test / Sandbox</option>
                                        <option value="production">Production</option>
                                    </select>
                                    {editingConfig && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Environment cannot be changed
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Integration Info */}
                            {PROVIDER_INTEGRATION_INFO[formData.provider] && (
                                <div className={`border-2 rounded-lg p-4 ${selectedProviderInfo?.supports_webhook_signature
                                    ? 'bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200'
                                    : 'bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-200'
                                    }`}>
                                    <div className="flex items-start gap-3">
                                        <div className={`p-2 rounded-lg flex-shrink-0 ${selectedProviderInfo?.supports_webhook_signature
                                            ? 'bg-purple-100'
                                            : 'bg-orange-100'
                                            }`}>
                                            <Info className={`w-5 h-5 ${selectedProviderInfo?.supports_webhook_signature
                                                ? 'text-purple-600'
                                                : 'text-orange-600'
                                                }`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className={`text-sm font-semibold mb-1 ${selectedProviderInfo?.supports_webhook_signature
                                                ? 'text-purple-900'
                                                : 'text-orange-900'
                                                }`}>
                                                {selectedProviderInfo?.supports_webhook_signature ? '🎯' : '⚠️'} Critical Integration Field
                                            </h4>
                                            <p className={`text-sm mb-3 ${selectedProviderInfo?.supports_webhook_signature
                                                ? 'text-purple-800'
                                                : 'text-orange-800'
                                                }`}>
                                                {PROVIDER_INTEGRATION_INFO[formData.provider].description}
                                            </p>
                                            <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-semibold text-gray-400 uppercase">
                                                        {PROVIDER_INTEGRATION_INFO[formData.provider].title}
                                                    </span>
                                                    {PROVIDER_INTEGRATION_INFO[formData.provider].field !== 'N/A' && (
                                                        <code className="text-xs text-yellow-400 bg-gray-800 px-2 py-1 rounded">
                                                            {PROVIDER_INTEGRATION_INFO[formData.provider].field}
                                                        </code>
                                                    )}
                                                </div>
                                                <pre className="text-xs text-gray-300 font-mono whitespace-pre-wrap">
                                                    {PROVIDER_INTEGRATION_INFO[formData.provider].code}
                                                </pre>
                                            </div>
                                            <p className={`text-xs mt-2 flex items-center gap-1 ${selectedProviderInfo?.supports_webhook_signature
                                                ? 'text-purple-700'
                                                : 'text-orange-700'
                                                }`}>
                                                <span>{selectedProviderInfo?.supports_webhook_signature ? '💡' : '⚠️'}</span>
                                                <span>
                                                    {selectedProviderInfo?.supports_webhook_signature
                                                        ? 'This field is used to match payments with your orders in silentloss'
                                                        : 'This provider does not support webhook signatures. Use SDK integration for security.'
                                                    }
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Webhook Secret */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Webhook Secret {selectedProviderInfo?.supports_webhook_signature && '*'}
                                </label>
                                <input
                                    type="password"
                                    value={formData.webhook_secret}
                                    onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
                                    placeholder="whsec_... / secret key / HMAC key"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                                    required={selectedProviderInfo?.supports_webhook_signature}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {selectedProviderInfo?.supports_webhook_signature
                                        ? 'Required for webhook signature validation'
                                        : 'Optional - only needed for signature verification'
                                    }
                                </p>
                            </div>

                            {/* Provider-Specific Settings */}
                            {settingsFields.length > 0 && (
                                <div className="border-t pt-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Info className="w-4 h-4 text-blue-600" />
                                        <h3 className="text-sm font-semibold text-gray-900">
                                            Provider-Specific Settings
                                        </h3>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        {settingsFields.map((field) => (
                                            <div key={field.key} className={field.key.includes('key') || field.key.includes('secret') ? 'col-span-2' : ''}>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {field.label}
                                                </label>
                                                <input
                                                    type={field.type}
                                                    value={formData.settings?.[field.key] || ''}
                                                    onChange={(e) => handleSettingChange(field.key, e.target.value)}
                                                    placeholder={field.placeholder}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                                />
                                                {field.help && (
                                                    <p className="text-xs text-gray-500 mt-1">{field.help}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Webhook URL Preview */}
                            {!editingConfig && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-start gap-2">
                                        <ExternalLink className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-blue-900 mb-1">
                                                Your Webhook URL
                                            </p>
                                            <code className="text-xs text-blue-700 break-all block">
                                                {providerData?.available.find(p => p.value === formData.provider)?.webhook_url || 'Loading...'}
                                            </code>
                                            <p className="text-xs text-blue-600 mt-2">
                                                Add this URL to your {selectedProviderInfo?.label || formData.provider} dashboard
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Active Toggle */}
                            <div className="flex items-center justify-between pt-4 border-t">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Active</label>
                                    <p className="text-xs text-gray-500">Enable webhook processing</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                </label>
                            </div>

                            {/* Provider Limit Warning in Modal */}
                            {!editingConfig && providerData?.limits && !providerData.limits.can_add_more && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start gap-2">
                                        <Info className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-red-900 mb-1">
                                                Provider Limit Reached
                                            </p>
                                            <p className="text-xs text-red-700">
                                                Your plan allows maximum {providerData.limits.max} providers. 
                                                Please upgrade to add more.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        setEditingConfig(null);
                                    }}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={updateMutation.isPending || (!editingConfig && !providerData?.limits?.can_add_more)}
                                    className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Save className="w-4 h-4" />
                                    {updateMutation.isPending ? 'Saving...' : editingConfig ? 'Update Provider' : 'Add Provider'}
                                </button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Providers;
